import { useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { Download, Printer, X } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

function QRCodeGenerator({ table, restaurantId, onClose }) {
  const [customerAppUrl, setCustomerAppUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomerAppUrl = async () => {
      try {
        // Try to get customer app URL from restaurant document first
        const restaurantDoc = await getDoc(doc(db, 'restaurants', restaurantId));
        const restaurantData = restaurantDoc.data();
        
        if (restaurantData?.customerAppUrl) {
          // Use URL from Firestore if set
          setCustomerAppUrl(restaurantData.customerAppUrl);
        } else {
          // Auto-detect based on current domain
          const currentDomain = window.location.origin;
          
          // If running locally, use env variable or localhost
          if (currentDomain.includes('localhost')) {
            setCustomerAppUrl(import.meta.env.VITE_CUSTOMER_APP_URL || 'http://localhost:5173');
          } else {
            // Auto-detect: replace 'admin' with 'customer' in domain
            // Works for patterns like:
            // - restaurant-qr-ordering-admin-app.vercel.app → restaurant-qr-ordering-customer-app.vercel.app
            // - admin.yourrestaurant.com → order.yourrestaurant.com
            let customerUrl = currentDomain;
            
            if (currentDomain.includes('admin')) {
              // Replace 'admin' with 'customer' or 'order'
              customerUrl = currentDomain.replace('admin-app', 'customer-app').replace('admin', 'order');
            } else if (import.meta.env.VITE_CUSTOMER_APP_URL) {
              // Fallback to env variable
              customerUrl = import.meta.env.VITE_CUSTOMER_APP_URL;
            }
            
            setCustomerAppUrl(customerUrl);
            console.log('Auto-detected customer app URL:', customerUrl);
          }
        }
      } catch (error) {
        console.error('Error fetching customer app URL:', error);
        // Ultimate fallback
        setCustomerAppUrl(import.meta.env.VITE_CUSTOMER_APP_URL || 'http://localhost:5173');
      } finally {
        setLoading(false);
      }
    };

    fetchCustomerAppUrl();
  }, [restaurantId]);

  const qrCodeUrl = `${customerAppUrl}/menu/${restaurantId}/${table.number}`;

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading QR Code...</p>
        </div>
      </div>
    );
  }
  
  const downloadQRCode = () => {
    const svg = document.getElementById(`qr-code-${table.id}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    canvas.width = 600;
    canvas.height = 700;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Draw QR code
      ctx.drawImage(img, 50, 100, 500, 500);
      
      // Add text
      ctx.fillStyle = 'black';
      ctx.font = 'bold 36px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(`Table ${table.number}`, 300, 60);
      
      ctx.font = '20px Arial';
      ctx.fillText('Scan to order', 300, 650);
      
      // Download
      const pngUrl = canvas.toDataURL('image/png');
      const downloadLink = document.createElement('a');
      downloadLink.href = pngUrl;
      downloadLink.download = `table-${table.number}-qr-code.png`;
      downloadLink.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const printQRCode = () => {
    const printWindow = window.open('', '', 'width=600,height=700');
    const svg = document.getElementById(`qr-code-${table.id}`);
    const svgData = new XMLSerializer().serializeToString(svg);
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Table ${table.number} QR Code</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              margin: 0;
              padding: 20px;
            }
            h1 {
              font-size: 48px;
              margin-bottom: 20px;
            }
            .qr-container {
              width: 400px;
              height: 400px;
              margin: 20px 0;
            }
            p {
              font-size: 24px;
              color: #666;
            }
            @media print {
              body {
                padding: 0;
              }
            }
          </style>
        </head>
        <body>
          <h1>Table ${table.number}</h1>
          <div class="qr-container">
            ${svgData}
          </div>
          <p>Scan to view menu and place order</p>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Table {table.number} QR Code</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* QR Code Preview */}
        <div className="bg-gray-50 p-8 rounded-lg mb-6 flex flex-col items-center">
          <QRCodeSVG
            id={`qr-code-${table.id}`}
            value={qrCodeUrl}
            size={300}
            level="H"
            includeMargin={true}
          />
          <p className="mt-4 text-sm text-gray-600 text-center">
            Customers scan this code to view menu and place orders
          </p>
        </div>

        {/* URL Display */}
        <div className="mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            QR Code URL:
          </label>
          <div className="bg-gray-50 p-3 rounded-lg">
            <code className="text-sm text-gray-800 break-all">{qrCodeUrl}</code>
          </div>
        </div>

        {/* Actions */}
        <div className="flex space-x-3">
          <button
            onClick={downloadQRCode}
            className="flex-1 btn-primary flex items-center justify-center space-x-2"
          >
            <Download className="w-5 h-5" />
            <span>Download PNG</span>
          </button>
          <button
            onClick={printQRCode}
            className="flex-1 btn-secondary flex items-center justify-center space-x-2"
          >
            <Printer className="w-5 h-5" />
            <span>Print</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default QRCodeGenerator;
